import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import LoxSelect from '../../../components/LoxSelect'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { CurrencyFormat } from '../../../helpers/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'

// Bulma gives field-label a flex-grow of 1 against field-body's 5, which wraps these labels.
const labelColumnStyle = { flexGrow: 2, whiteSpace: 'nowrap' }

/**
 * "Customer Declined" capture modal for Verification Step 2: read-only offered amount + the Ideal
 * Payment Amount. Blank on open, Save disabled until an amount is picked; capture only (parent POSTs).
 */
export default function CustomerDeclinedModal({
  isOpen,
  offeredPaymentAmount,
  idealPaymentAmountOptions,
  isSaving,
  onClose,
  onSave,
}) {
  const [selectedAmount, setSelectedAmount] = useState(null)

  // Always blank when the modal opens — never pre-filled from an earlier save.
  useEffect(() => {
    if (isOpen) {
      setSelectedAmount(null)
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  // Portalled to <body>, as PaymentScheduleOptions and ViewSensitivePiiButtons do — rendered in
  // place, an ancestor's containing block traps the fixed backdrop and the page stays clickable.
  return createPortal(
    <div className="modal is-active" data-testid="customer-declined-modal">
      {/* Inert, like the Quick Action modals — the backdrop dims but does not dismiss. */}
      <div className="modal-background" data-testid="customer-declined-backdrop" />
      <div className="modal-card column is-10-tablet is-5-fullhd is-12-mobile">
        <header className="modal-card-head">
          <p className="modal-card-title">Ideal Payment Amount</p>
          <button
            type="button"
            className="delete"
            aria-label="close"
            onClick={onClose}
          />
        </header>
        <section className="modal-card-body">
          <div className="field is-horizontal">
            <div className="field-label is-normal" style={labelColumnStyle}>
              <label className="label">Offered Payment Amount:</label>
            </div>
            <div className="field-body is-align-items-center">
              <span data-testid="customer-declined-offered-amount">
                <CurrencyFormat value={offeredPaymentAmount} />
              </span>
            </div>
          </div>
          <div className="field is-horizontal">
            <div className="field-label is-normal" style={labelColumnStyle}>
              <label className="label">
                Ideal Payment Amount:
                <span
                  className="lox-no-border ml-1 has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary has-text-left"
                  data-tooltip="The payment amount the customer said they were targeting when they declined the offer."
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="has-text-link"
                  />
                </span>
              </label>
            </div>
            <div className="field-body">
              <div className="field" style={{ width: '100%' }}>
                <LoxSelect
                  testId="ideal-payment-amount"
                  inputId="ideal-payment-amount-select"
                  aria-label="Ideal Payment Amount"
                  classNames={reactSelectClassNames()}
                  options={idealPaymentAmountOptions}
                  value={selectedAmount}
                  onChange={setSelectedAmount}
                  isSearchable={false}
                  menuPlacement="auto"
                  // Portal to <body> so the long amount list isn't clipped/covered by the modal card.
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                  placeholder=""
                />
              </div>
            </div>
          </div>
        </section>
        <footer className="modal-card-foot is-justify-content-end py-1">
          <div className="lox-form__buttons-wrapper has-text-right">
            <button
              type="button"
              className="button is-small has-background-grey has-text-white mx-1"
              data-testid="customer-declined-cancel"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="button is-small is-link"
              data-testid="customer-declined-save"
              disabled={!selectedAmount || isSaving}
              onClick={() => onSave(selectedAmount.value)}
            >
              Save
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  )
}
